import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { initialLoading, showRegistrationPopup } from '~/actions';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useTrackEvent } from '~/hooks/useTrackEvent';
import { POSTHOG_EVENTS } from '~/services/posthog';
import { apiMethodsConst } from '~/services/methods';
import { SaveWorkerTypeService } from '../services/SaveWorkerTypeService';
import type { ISaveWorkerTypeRequest } from '../types/SaveWorkerType';
import { suppressPostSignupPopups } from '../utils/suppressPostSignupPopups';

export const useSaveWorkerType = () => {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const currentUser = useAppSelector((state) => state.user?.user ?? null);

  return useMutation<void, unknown, ISaveWorkerTypeRequest>({
    mutationFn: (payload) => SaveWorkerTypeService.saveWorkerType(payload),
    onSuccess: (_data, { workerType, psaAccepted }) => {
      if (!currentUser?.id) return;

      const existingOfficeInfo = currentUser.office_info ?? {};
      const existingPsaTerms =
        (existingOfficeInfo as { psaTerms?: { accepted?: boolean } }).psaTerms ?? {};

      const workerClassificationUpdate =
        workerType === 'contractor' || workerType === 'employee'
          ? { workerClassification: { classificationType: workerType } }
          : {};

      const psaUpdate =
        workerType === 'employee' && psaAccepted
          ? {
              office_info: {
                ...existingOfficeInfo,
                psaTerms: { ...existingPsaTerms, accepted: true },
              },
            }
          : {};

      dispatch({
        type: apiMethodsConst.EDIT_USER_REQUEST,
        payload: {
          ...currentUser,
          is_profile_complete: true,
          ...workerClassificationUpdate,
          ...psaUpdate,
        },
      });

      suppressPostSignupPopups(String(currentUser.id));
      trackEvent(POSTHOG_EVENTS.DENTAL_PORTAL_OFFICE_SIGNUP_COMPLETED);
      dispatch(showRegistrationPopup());
      // Re-run watchInitialLoading so PostHog identifies the now-complete
      // user (TnCOffices FF flips on, OFFICE_TNC consent popup can show)
      // and userCredentials/officeEducation refresh from BE.
      dispatch(initialLoading());
    },
  });
};
